import type { Directive, DirectiveBinding } from 'vue'
import { trackUserAction } from '@/observability/sentry'
import log from '@/utils/useLogger'

interface TrackOptions {
  action: string
  category?: string
  data?: Record<string, any>
}

/**
 * Vue directive for automatic click tracking
 *
 * Usage:
 * v-track="'Button Name'"  - Simple tracking with button name
 * v-track="{ action: 'Clicked Submit', category: 'form', data: { formId: 123 } }" - Full options
 *
 * Examples:
 * <button v-track="'Save Mission'">Save</button>
 * <button v-track="{ action: 'Launch Drone', category: 'drone', data: { droneId } }">Launch</button>
 */
export const vTrack: Directive<HTMLElement, string | TrackOptions> = {
  mounted(el: HTMLElement, binding: DirectiveBinding<string | TrackOptions>) {
    const handler = () => {
      const value = binding.value

      if (typeof value === 'string') {
        // Simple string tracking
        trackUserAction(`Clicked: ${value}`, 'button.click', {
          element: el.tagName.toLowerCase(),
          text: el.textContent?.trim().substring(0, 50),
        })
        log.debug(`[Track] Clicked: ${value}`)
      } else if (value && typeof value === 'object') {
        // Full options object
        trackUserAction(
          value.action,
          value.category || 'button.click',
          {
            element: el.tagName.toLowerCase(),
            text: el.textContent?.trim().substring(0, 50),
            ...value.data,
          }
        )
        log.debug(`[Track] ${value.action}`, value.data)
      }
    }

    el.addEventListener('click', handler)
    // Store handler reference for cleanup
    ;(el as any)._trackHandler = handler
  },

  unmounted(el: HTMLElement) {
    const handler = (el as any)._trackHandler
    if (handler) {
      el.removeEventListener('click', handler)
      delete (el as any)._trackHandler
    }
  },
}

export default vTrack
